
import { Button } from '@/components/ui/button';
import { useRef, useState, useEffect } from 'react';


/* ─── 3B: Advantages (alternating blocks data) ─── */
const advantages = [
  {
    label: 'Manual, Line-by-Line Code Review',
    headline: 'Every. Single. Line.',
    description: 'We don\'t just run Slither and ship a PDF. Every line of your smart contract is reviewed manually by a senior security engineer. Automated tools catch ~30% of vulnerabilities. Our auditors catch the other 70%.',
    contrast: 'Most audit firms rely heavily on automated scanners. We use them as step one - not the whole process.',
    visual: 'code',
  },
  {
    label: 'Speed Without Compromise',
    headline: 'Fast audits. No shortcuts.',
    description: 'In Web3, timing is everything. Launch windows are tight, TVL is waiting, and every day without an audit is a day of risk. We deliver thorough audits on aggressive timelines - without cutting corners.',
    contrast: 'Other firms quote 4-6 weeks. We work to match your launch schedule.',
    visual: 'speed',
  },
  {
    label: 'Live Tracking Platform',
    headline: 'Process is always visible.',
    description: 'Every finding, its severity, and its status - available on our secure platform the moment it\'s discovered. No need to chase your auditor for updates or wait for a final PDF to know where things stand. You see what we see, as we work.',
    contrast: 'Other firms: radio silence for weeks, then a 200-page PDF. BugBlow: real-time visibility from day one.',
    visual: 'dashboard',
  },
];

/* ─── Visual placeholders for 3B ─── */
const CodeVisual = () => (
  <div className="rounded-xl bg-secondary/50 border border-border p-5 font-mono text-xs leading-6 text-muted-foreground overflow-hidden">
    <div className="text-foreground/40 mb-1">// Signature Replay Critical Vulnerability</div>
    <div><span className="text-primary/60">function</span> <span className="text-foreground">claim</span>(<span className="text-primary/60">uint256</span> _amount, <span className="text-primary/60">bytes memory</span> _sig) <span className="text-primary/60">external</span> {'{'}</div>
    <div>  bytes32 message = prefixed(</div>
    <div className="bg-destructive/15 border-l-2 border-destructive pl-3 -ml-1 text-foreground">    keccak256(abi.encodePacked(msg.sender, _amount, userNonce[msg.sender]))</div>
    <div>  );</div>
    <div>  require(recoverSigner(message, _sig) == signer, <span className="text-primary">"Invalid signature"</span>);</div>
    <div className="mt-2" />
    <div>  (bool sent, ) = msg.sender.call{'{'}value: _amount{'}'}("");</div>
    <div>  require(sent, <span className="text-primary">"Failed to send"</span>);</div>
    <div>{'}'}</div>
  </div>
);

const SpeedVisual = () => (
  <div className="rounded-xl bg-secondary/50 border border-border p-5 space-y-3">
    {[
      { label: 'Day 1-2', desc: 'Scope & kickoff', pct: 20 },
      { label: 'Day 3-7', desc: 'Manual review', pct: 55 },
      { label: 'Day 7-8', desc: 'Report delivery', pct: 80 },
    ].map((s) => (
      <div key={s.label}>
        <div className="flex justify-between text-xs mb-1">
          <span className="text-foreground font-medium font-numbers">{s.label}</span>
          <span className="text-muted-foreground">{s.desc}</span>
        </div>
        <div className="h-1.5 rounded-full bg-border overflow-hidden">
          <div className="h-full rounded-full bg-primary/70 transition-all duration-1000" style={{ width: `${s.pct}%` }} />
        </div>
      </div>
    ))}
    <p className="text-xs text-primary/80 text-center pt-1 font-numbers">~8 days vs industry avg 4-6 weeks</p>
  </div>
);

const auditFindings = [
  { severity: 'Critical', title: 'Reentrancy in withdraw()', status: 'Found', color: 'bg-red-500/20 text-red-400' },
  { severity: 'Critical', title: 'Unprotected selfdestruct()', status: 'Found', color: 'bg-red-500/20 text-red-400' },
  { severity: 'Critical', title: 'Delegatecall injection', status: 'Found', color: 'bg-red-500/20 text-red-400' },
  { severity: 'Critical', title: 'Flash loan price manipulation', status: 'Found', color: 'bg-red-500/20 text-red-400' },
  { severity: 'High', title: 'Unchecked external call', status: 'Found', color: 'bg-orange-500/20 text-orange-400' },
  { severity: 'High', title: 'Integer overflow in mint()', status: 'Fixed', color: 'bg-orange-500/20 text-orange-400' },
  { severity: 'High', title: 'Missing slippage protection', status: 'Found', color: 'bg-orange-500/20 text-orange-400' },
  { severity: 'High', title: 'Front-running vulnerability', status: 'Fixed', color: 'bg-orange-500/20 text-orange-400' },
  { severity: 'Medium', title: 'Missing access check', status: 'Fixed', color: 'bg-yellow-500/20 text-yellow-400' },
  { severity: 'Medium', title: 'Timestamp dependence', status: 'Ack', color: 'bg-yellow-500/20 text-yellow-400' },
  { severity: 'Medium', title: 'Centralization risk in owner()', status: 'Fixed', color: 'bg-yellow-500/20 text-yellow-400' },
  { severity: 'Medium', title: 'Unsafe ERC20 transfer', status: 'Found', color: 'bg-yellow-500/20 text-yellow-400' },
  { severity: 'Medium', title: 'Missing event emission', status: 'Fixed', color: 'bg-yellow-500/20 text-yellow-400' },
  { severity: 'Medium', title: 'Unbounded loop in distribute()', status: 'Found', color: 'bg-yellow-500/20 text-yellow-400' },
  { severity: 'Low', title: 'Floating pragma version', status: 'Ack', color: 'bg-blue-500/20 text-blue-400' },
  { severity: 'Low', title: 'Missing zero-address check', status: 'Fixed', color: 'bg-blue-500/20 text-blue-400' },
  { severity: 'Low', title: 'Redundant state variable', status: 'Ack', color: 'bg-blue-500/20 text-blue-400' },
  { severity: 'Info', title: 'Gas optimization', status: 'Ack', color: 'bg-blue-500/20 text-blue-400' },
  { severity: 'Info', title: 'Unused import statement', status: 'Ack', color: 'bg-blue-500/20 text-blue-400' },
  { severity: 'Info', title: 'Consider using immutable', status: 'Ack', color: 'bg-blue-500/20 text-blue-400' },
];

// Group by severity tier so we always show diverse colors
const tierHigh = auditFindings.filter(f => f.severity === 'Critical' || f.severity === 'High');
const tierMid = auditFindings.filter(f => f.severity === 'Medium');
const tierLow = auditFindings.filter(f => f.severity === 'Low' || f.severity === 'Info');

function pickRandom<T>(arr: T[], exclude?: Set<string>, key?: (item: T) => string): T {
  if (exclude && key) {
    const available = arr.filter(item => !exclude.has(key(item)));
    if (available.length > 0) return available[Math.floor(Math.random() * available.length)];
  }
  return arr[Math.floor(Math.random() * arr.length)];
}

const DashboardVisual = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  // Always start with one from each tier
  const [items, setItems] = useState(() => [tierHigh[0], tierMid[0], tierLow[0]]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => setIsVisible(entry.isIntersecting),
      { threshold: 0.3 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;
    const interval = setInterval(() => {
      setItems(prev => {
        const slotIndex = Math.floor(Math.random() * 3);
        const exclude = new Set(prev.map(p => p.title));
        // Pick from the correct tier for this slot
        const tier = slotIndex === 0 ? tierHigh : slotIndex === 1 ? tierMid : tierLow;
        const newItem = pickRandom(tier, exclude, f => f.title);
        const next = [...prev];
        next[slotIndex] = newItem;
        return next;
      });
    }, 15000);
    return () => clearInterval(interval);
  }, [isVisible]);

  return (
    <div ref={ref} className="rounded-xl bg-secondary/50 border border-border p-5 space-y-2 text-xs">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-2 h-2 rounded-full bg-primary animate-[blink_1.5s_ease-in-out_infinite]" />
        <span className="text-foreground font-medium">Live Audit Board</span>
      </div>
      {items.map((item, i) => (
        <div
          key={`${i}-${item.title}`}
          className="flex items-center justify-between rounded-lg bg-background/50 border border-border px-3 py-2 animate-fade-in"
        >
          <div className="flex items-center gap-2">
            <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${item.color}`}>{item.severity}</span>
            <span className="text-foreground/80">{item.title}</span>
          </div>
          <span className="text-muted-foreground text-[10px]">{item.status}</span>
        </div>
      ))}
    </div>
  );
};

const PriceVisual = () => (
  <div className="rounded-xl bg-secondary/50 border border-border p-6 flex flex-col items-center">
    <div className="flex items-end justify-center gap-10 mb-4">
      <div className="flex flex-col items-center gap-2">
        <span className="text-xs text-muted-foreground font-medium">Industry average</span>
        <div className="w-16 bg-muted rounded-t-md flex items-center justify-center" style={{ height: '120px' }} />
        <span className="text-sm font-bold text-foreground/70 whitespace-nowrap font-numbers">$10,000+</span>
      </div>
      <div className="flex flex-col items-center gap-2">
        <span className="text-xs text-primary font-medium">BugBlow</span>
        <div className="w-16 bg-primary/70 rounded-t-md flex items-center justify-center" style={{ height: '40px' }} />
        <span className="text-[11px] font-bold text-foreground whitespace-nowrap font-numbers">from $3,000</span>
      </div>
    </div>
    <p className="text-[11px] text-muted-foreground text-center">Starting price for a smart contract audit</p>
  </div>
);

const visuals: Record<string, React.FC> = {
  code: CodeVisual,
  speed: SpeedVisual,
  dashboard: DashboardVisual,
  price: PriceVisual,
};

/* ─── Scroll fade helper ─── */
function FadeIn({ children, className = '', delay = 0, direction = 'up' }: { children: React.ReactNode; className?: string; delay?: number; direction?: 'up' | 'left' | 'right' }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold: 0.15 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const translate = direction === 'left' ? '-translate-x-8' : direction === 'right' ? 'translate-x-8' : 'translate-y-6';

  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${visible ? 'opacity-100 translate-x-0 translate-y-0' : `opacity-0 ${translate}`} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

interface ProtectionSectionProps {
  onRequestAudit: () => void;
}

const ProtectionSection = ({ onRequestAudit }: ProtectionSectionProps) => {

  return (
    <section className="py-[75px] relative" id="protection">
      <div className="container mx-auto px-4 lg:px-8">


        {/* ── 3B: Alternating blocks ── */}
        <div className="relative">
          <div className="text-center mb-[55px] relative z-10">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-light tracking-tight mb-[35px]">
              Why teams choose <span className="text-gradient-red font-normal">BugBlow</span>
            </h2>
          </div>

          <div className="space-y-20 lg:space-y-28 relative z-10">
            {advantages.map((adv, i) => {
              const isEven = i % 2 === 0;
              const Visual = visuals[adv.visual];
              return (
                <div key={adv.label} className={`flex flex-col ${isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-8 lg:gap-16 items-center`}>
                  {/* Text side */}
                  <FadeIn className="flex-1" direction={isEven ? 'left' : 'right'} delay={100}>
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-xs font-medium text-primary uppercase tracking-wider">{adv.label}</span>
                    </div>
                    <h3 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground mb-4">{adv.headline}</h3>
                    <p className="text-muted-foreground leading-relaxed mb-5 font-numbers">{adv.description}</p>
                    <div className="rounded-xl bg-secondary/30 border border-border px-5 py-4">
                      <p className="text-sm text-muted-foreground italic">{adv.contrast}</p>
                    </div>
                  </FadeIn>

                  {/* Visual side */}
                  <FadeIn className="flex-1 w-full" direction={isEven ? 'right' : 'left'} delay={250}>
                    <Visual />
                  </FadeIn>
                </div>
              );
            })}
          </div>

          {/* CTA */}
          <div className="text-center mt-16 lg:mt-24 relative z-10">
            <Button
              size="lg"
              className="btn-primary-filled rounded-full px-10 py-7 text-lg sm:px-8 sm:py-6 sm:text-base glow-red"
              onClick={onRequestAudit}
            >
              Get a Quote
            </Button>
            <p className="text-sm text-muted-foreground mt-4">Free initial consultation. No commitment.</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProtectionSection;
